import React from 'react';
import { observer } from 'mobx-react';
import get from 'lodash/get';
import loadable from '@loadable/component';
import classNames from 'classnames';

import pageStore from 'stores/PageStore';
import previewStore from 'stores/PreviewStore';
import visitorStore from 'stores/Visitor/VisitorStore';
import payloadStore from 'stores/PayloadStore';

import * as api from 'Api';
import constants from 'Constants';
import * as utils from 'Utils';
import * as promptUtils from 'stores/Prompt/Utils';
import './styles.scss';




function loadFonts(options) {
  let headEl = document.getElementsByTagName('head')[0];
  const gFontOptions = options.filter((option) => option.is_google_font);
  gFontOptions.forEach((option) => {
    let accessor = option.family.toLowerCase().replaceAll(" ", "_");
    let elementId = `ov_font_${accessor}`;

    let existingTag = document.getElementById(elementId);
    if (existingTag) return;

    let tag = document.createElement('link');
    tag.id = elementId;
    tag.rel = "stylesheet";
    tag.href = `https://fonts.googleapis.com/css2?family=${option.family}`;
    headEl.appendChild(tag);
  })
}


// Style Utils
function isSelector(key) {
  key = key.trim();
  if (key.startsWith('#')) return true;
  if (key.startsWith('.')) return true;
  if (key.startsWith('&')) return true;
  if (key.startsWith(':')) return true;
  return false
}

function cleanKey(key) {
  key = key.replace(" &", "");
  key = key.trim();
  return key
}

function flatten(object) {
  let output = {};

  function step(node, parentKey="") {
    Object.keys(node).forEach(function(key) {
      const value = node[key];
      const attributes = {};
      const children = {};

      const type = Object.prototype.toString.call(value);
      const isObject = (
        type === '[object Object]' ||
        type === '[object Array]'
      );

      if (isObject) {
        Object.keys(value).forEach(function(rowKey) {
          const rowValue = value[rowKey];
          if (!isSelector(rowKey)) {
            attributes[rowKey] = rowValue;
          }

          if (isSelector(rowKey)) {
            let stepKey = `${key} ${rowKey}`;
            if (parentKey) stepKey = `${parentKey} ${key} ${rowKey}`;
            step(rowValue, stepKey);
          }
        })
      }

      if (!isObject) {
        attributes[key] = value;
      }

      let outputKey = parentKey;
      if (isObject) outputKey = `${parentKey} ${key}`;
      outputKey = cleanKey(outputKey);
      output[outputKey] = attributes;
    })
  }

  step(object);
  return output;
}

function removeEmptyCssAttribute(obj) {
  Object.keys(obj).forEach(k =>
    (obj[k] && typeof obj[k] === 'object') && removeEmptyCssAttribute(obj[k]) ||
    (obj[k] === "" || obj[k] === null) && delete obj[k]
  );
  return obj;
};

export function jsonToScss(object, config={}) {
  let removeEmpty = get(config, 'removeEmpty', true);

  if (removeEmpty) {
    object = removeEmptyCssAttribute(object);
  }

  let text = JSON.stringify(object, null, 2);
  let pattern = /({)([\s\S]*)(})/gm;
  text = text.replace(pattern, '$2');

  pattern = /("[^"]*":[ "]"[^"]*")([,]*)/gm;
  text = text.replace(pattern, '$1;');

  pattern = /(}),/gm;
  text = text.replace(pattern, '$1');

  pattern = /(:)([ ]*{)/gm;
  text = text.replace(pattern, '$2');

  pattern = /"([^"]*)":[ "]"([^"]*)"/gm;
  text = text.replace(pattern, '$1: $2');

  pattern = /"([^"]*)"[ ]*{/gm;
  text = text.replace(pattern, '$1 {');
  text = text.trim();
  return text;
}

function jsonToCss(object) {
  object = removeEmptyCssAttribute(object);
  let flattenedObject = flatten(object);
  return jsonToScss(flattenedObject);
}


function generatePromptDesktopStyles(prompt, page) {
  const overlay = page.overlay;
  const closeButton = page.closeButton;
  const content = page.content;

  const style = {};
  style[`.ov-prompt #${content.containerId}`] = content.containerCssAttributes;
  style[`.ov-prompt #${content.wrapperId}`] = content.wrapperCssAttributes;
  style[`.ov-prompt #${content.id}`] = content.cssAttributes;

  style[`.ov-prompt #${overlay.id}`] = page.overlayCssAttributes;
  style[`.ov-prompt #${closeButton.id}`] = closeButton.cssAttributes;
  style[`.ov-prompt #${closeButton.id} svg`] = closeButton.iconCssAttributes;
  style[`.ov-prompt #${closeButton.id} svg:hover`] = closeButton.iconHoveredCssAttributes;

  content.sectors.forEach((sector, sectorIdx) => {
    const sectorCssAttributes = sector.cssAttributes;
    const sectorWidth = content.sectorWidths[sectorIdx];
    sectorCssAttributes['width'] = `${sectorWidth}%`;
    sectorCssAttributes['min-height'] = content.minHeightCssValue;
    style[`.ov-prompt #${sector.id}`] = sectorCssAttributes;

    sector.rows.forEach((row, rowIdx) => {
      style[`.ov-prompt #${row.id}`] = row.cssAttributes;

      row.columns.forEach((column, columnIdx) => {
        const columnCssAttributes = column.cssAttributes;
        const columnWidth = row.columnWidths[columnIdx];
        columnCssAttributes['width'] = `${columnWidth}%`;
        style[`.ov-prompt #${column.id}`] = columnCssAttributes;

        column.blocks.forEach((block, blockIdx) => {
          style[`.ov-prompt #${block.id}`] = block.blockCssAttributes;

          if (!block.data) return;
          if (block.isTypeText) {
            style[`.ov-prompt #${block.id} .ov-text-block .ov-html-content`] = block.data.cssAttributes;
          }

          if (block.isTypeButton) {
            style[`.ov-prompt #${block.id} .ov-button-block`] = block.data.buttonBlockCssAttributes;
            style[`.ov-prompt #${block.id} .ov-button-block a`] = block.data.cssAttributes;
          }

          if (block.isTypeImage) {
            style[`.ov-prompt #${block.id} .ov-image-block`] = block.data.imageBlockCssAttributes;
            style[`.ov-prompt #${block.id} .ov-image-block img`] = block.data.cssAttributes;
          }

          if (block.isTypeDivider) {
            style[`.ov-prompt #${block.id} .ov-divider-block hr`] = block.data.cssAttributes;
          }

          if (block.isTypeSpacer) {
            style[`.ov-prompt #${block.id} .ov-spacer-block`] = block.data.spacerBlockCssAttributes;
          }

          if (block.isTypeInput) {
            style[`.ov-prompt #${block.id} .ov-input-block`] = block.data.inputBlockCssAttributes;
            style[`.ov-prompt #${block.id} .ov-input-block input`] = block.data.cssAttributes;
            style[`.ov-prompt #${block.id} .ov-input-block input::placeholder`] = block.data.placeholderCssAttributes;
          }

          if (block.isTypeTextarea) {
            style[`.ov-prompt #${block.id} .ov-textarea-block`] = block.data.textareaBlockCssAttributes;
            style[`.ov-prompt #${block.id} .ov-textarea-block textarea`] = block.data.cssAttributes;
            style[`.ov-prompt #${block.id} .ov-textarea-block textarea::placeholder`] = block.data.placeholderCssAttributes;
          }

          if (block.isTypeSelect) {
            style[`.ov-prompt #${block.id} .ov-select-block`] = block.data.selectBlockCssAttributes;
            style[`.ov-prompt #${block.id} .ov-select-block .ov-select`] = block.data.cssAttributes;
            style[`.ov-prompt #${block.id} .ov-select-block .ov-select-selection`] = block.data.selectionCssAttributes;
            style[`.ov-prompt #${block.id} .ov-select-block .ov-select-selection__rendered`] = block.data.selectionRenderedCssAttributes;
            style[`.ov-prompt #${block.id} .ov-select-block .ov-select-selection__clear`] = block.data.selectionClearCssAttributes;
            style[`.ov-prompt #${block.id} .ov-select-block .ov-select-arrow`] = block.data.selectArrowCssAttributes;
            style[`.ov-prompt #${block.id} .ov-select-block .ov-select-selection__placeholder`] = block.data.placeholderCssAttributes;
          }

          if (block.isTypeCheckbox) {
            style[`.ov-prompt #${block.id} .ov-checkbox-block`] = block.data.checkboxBlockCssAttributes;
            style[`.ov-prompt #${block.id} .ov-checkbox-block .ov-checkbox-group`] = block.data.groupCssAttributes;
            style[`.ov-prompt #${block.id} .ov-checkbox-block .ov-checkbox`] = block.data.checkboxCssAttributes;
            style[`.ov-prompt #${block.id} .ov-checkbox-block .ov-checkbox.ov-checked .ov-checkbox-indicator`] = block.data.indicatorCssAttributes;
          }

          if (block.isTypeRadio) {
            style[`.ov-prompt #${block.id} .ov-radio-block`] = block.data.radioBlockCssAttributes;
            style[`.ov-prompt #${block.id} .ov-radio-block .ov-radio-group`] = block.data.groupCssAttributes;
            style[`.ov-prompt #${block.id} .ov-radio-block .ov-radio-option`] = block.data.optionCssAttributes;
            style[`.ov-prompt #${block.id} .ov-radio-block .ov-radio-option input:checked + label::before`] = block.data.selectedBorderCssAttributes;
            style[`.ov-prompt #${block.id} .ov-radio-block .ov-radio-option label::after`] = block.data.selectedIndicatorCssAttributes;
          }

          if (block.isTypeDate) {
            style[`.ov-prompt #${block.id} .ov-date-block .react-datepicker__input-container`] = block.data.containerCssAttributes;
            style[`.ov-prompt #${block.id} .ov-date-block input`] = block.data.cssAttributes;
            style[`.ov-prompt #${block.id} .ov-date-block input::placeholder`] = block.data.placeholderCssAttributes;
          }

          if (block.isTypeCountdown) {
            style[`.ov-prompt #${block.id} .ov-countdown-block`] = block.data.countdownBlockCssAttributes;
            style[`.ov-prompt #${block.id} .ov-countdown-block .ov-countdown-item`] = block.data.itemCssAttributes;
            style[`.ov-prompt #${block.id} .ov-countdown-block .ov-item-number`] = block.data.numberCssAttributes;
            style[`.ov-prompt #${block.id} .ov-countdown-block .ov-item-unit`] = block.data.unitCssAttributes;
          }

          if (block.isTypeProductList) {
            style[`.ov-prompt #${block.id} .ov-product-list-block`] = block.data.productListBlockCssAttributes;
            utils.loopObject(block.productListBlock.styles, (key, value) => {
              style[`.ov-prompt #${block.id} .ov-product-list-block ${key}`] = value;
            })
          }

          if (block.isTypeVideo) {
            style[`.ov-prompt #${block.id} .ov-video-block`] = block.data.videoBlockCssAttributes;
          }
        })
      })
    })
  })

  page.floatingBlocks.forEach((block, blockIdx) => {
    style[`.ov-prompt #${block.id}`] = block.blockCssAttributes;

    if (!block.data) return;
    if (block.isTypeFloatingImage) {
      style[`.ov-prompt #${block.id} .ov-floating-image-block`] = block.data.floatingImageBlockCssAttributes;
      style[`.ov-prompt #${block.id} .ov-floating-image-block img`] = block.data.cssAttributes;
    }
  });


  // Launcher
  const launcher = prompt.launcher;
  const launcherCloseButton = launcher.closeButton;
  style[`.ov-prompt #${launcher.id}`] = launcher.cssAttributes;
  style[`.ov-prompt #${launcher.containerId}`] = launcher.containerCssAttributes;

  style[`.ov-prompt #${launcherCloseButton.id}`] = launcherCloseButton.cssAttributes;
  style[`.ov-prompt #${launcherCloseButton.id} svg`] = launcherCloseButton.iconCssAttributes;
  style[`.ov-prompt #${launcherCloseButton.id} svg:hover`] = launcherCloseButton.iconHoveredCssAttributes;

  launcher.rows.forEach((row, rowIdx) => {
    style[`.ov-prompt #${row.id}`] = row.cssAttributes;
    row.blocks.forEach((block, blockIdx) => {
      style[`.ov-prompt #${block.id}`] = block.blockCssAttributes;

      if (!block.data) return;
      if (block.isTypeText) {
        style[`.ov-prompt #${block.id} .ov-text-block .ov-html-content`] = block.data.cssAttributes;
      }

      if (block.isTypeButton) {
        style[`.ov-prompt #${block.id} .ov-button-block`] = block.data.buttonBlockCssAttributes;
        style[`.ov-prompt #${block.id} .ov-button-block a`] = block.data.cssAttributes;
      }

      if (block.isTypeImage) {
        style[`.ov-prompt #${block.id} .ov-image-block`] = block.data.imageBlockCssAttributes;
        style[`.ov-prompt #${block.id} .ov-image-block img`] = block.data.cssAttributes;
      }
    })
  })

  launcher.floatingBlocks.forEach((block, blockIdx) => {
    style[`.ov-prompt #${block.id}`] = block.blockCssAttributes;

    if (!block.data) return;
    if (block.isTypeFloatingImage) {
      style[`.ov-prompt #${block.id} .ov-floating-image-block`] = block.data.floatingImageBlockCssAttributes;
      style[`.ov-prompt #${block.id} .ov-floating-image-block img`] = block.data.cssAttributes;
    }
  });


  const cssStyles = jsonToCss(style);
  return cssStyles;
}

function generatePromptMobileStyles(prompt, page) {
  const overlay = page.overlay;
  const closeButton = page.closeButton;
  const content = page.content;

  const style = {};
  style[`.ov-prompt #${content.containerId}`] = content.mobileContainerCssAttributes;
  style[`.ov-prompt #${content.wrapperId}`] = content.mobileWrapperCssAttributes;
  style[`.ov-prompt #${content.id}`] = content.mobileCssAttributes;

  content.sectors.forEach((sector, sectorIdx) => {
    const sectorMobileCssAttributes = sector.mobileCssAttributes;
    style[`.ov-prompt #${sector.id}`] = sector.mobileCssAttributes;

    sector.rows.forEach((row, rowIdx) => {
      style[`.ov-prompt #${row.id}`] = row.mobileCssAttributes;

      row.columns.forEach((column, columnIdx) => {
        style[`.ov-prompt #${column.id}`] = column.mobileCssAttributes;

        column.blocks.forEach((block, blockIdx) => {
          style[`.ov-prompt #${block.id}`] = block.mobileBlockCssAttributes;

          if (!block.data) return;
          if (block.isTypeSpacer) {
            style[`.ov-prompt #${block.id} .ov-spacer-block`] = block.data.mobileSpacerBlockCssAttributes;
          }

        })
      })
    })
  })

  page.floatingBlocks.forEach((block, blockIdx) => {
    style[`.ov-prompt #${block.id}`] = block.mobileBlockCssAttributes;
  });

  // Launcher
  const launcher = prompt.launcher;
  const launcherCloseButton = launcher.closeButton;

  style[`.ov-prompt #${launcher.id}`] = launcher.mobileCssAttributes;
  style[`.ov-prompt #${launcher.containerId}`] = launcher.mobileContainerCssAttributes;
  style[`.ov-prompt #${launcherCloseButton.id}`] = launcherCloseButton.mobileCssAttributes;

  launcher.rows.forEach((row, rowIdx) => {
    style[`.ov-prompt #${row.id}`] = row.mobileCssAttributes;
    row.blocks.forEach((block, blockIdx) => {
      style[`.ov-prompt #${block.id}`] = block.mobileBlockCssAttributes;
    })
  })

  launcher.floatingBlocks.forEach((block, blockIdx) => {
    style[`.ov-prompt #${block.id}`] = block.mobileBlockCssAttributes;
  });

  const cssStyles = jsonToCss(style);
  let mobileStyles = `
    @media only screen and (max-width: 767px) {
        ${cssStyles}
    }
  `
  return mobileStyles;
}


function generatePromptStyles(prompt, page) {
  let desktopStyles = generatePromptDesktopStyles(prompt, page);
  let mobileStyles = generatePromptMobileStyles(prompt, page);
  let styles = `${desktopStyles}\n\n${mobileStyles}`;
  return styles;
}


@observer
class Prompt extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      error: null
    };
  }

  componentDidMount() {
    const { prompt } = this.props;
    this.updatePromptStyles();
    this.trackView();
  }

  componentDidCatch(error, info) {
    this.setState({error: error});
  }

  trackView = () => {
    const { prompt } = this.props;
    if (!utils.isClientProduction()) return;
    if (previewStore.active) return;
    if (!prompt.display) return;

    const viewedPrompts = window.OnVoardPrompt['viewed_prompts'] || [];
    if (viewedPrompts.includes(prompt.id)) return;

    let numViews = 0;
    let promptActivity = visitorStore.getPromptActivity(prompt.id);
    if (promptActivity && promptActivity.numViews) numViews = promptActivity.numViews;
    numViews += 1;

    let data = {};
    data['numViews'] = numViews;
    data['lastSeen'] = utils.getCurrentTimestamp();
    visitorStore.updatePromptActivity(prompt.id, data);

    // track impression
    let payload = {};
    payload['prompt'] = prompt.id;

    const successCallback = (response) => {};
    const errorCallback = (error) => {
      console.error(error);
    };

    api.postPromptTrackImpression(successCallback, errorCallback, JSON.stringify(payload));

    // web tracking
    prompt.triggerWebTrackingSeen();

    // callback
    utils.executeCallback(pageStore.onTrackPromptImpressionCallback, prompt.id);

    window.OnVoardPrompt['viewed_prompts'].push(prompt.id);
  };

  updatePromptStyles = () => {
    const { prompt } = this.props;
    loadFonts(prompt.loadableFontOptions);

    let el = document.querySelector(".ov-app-prompt");
    if (!el) return null;

    const currentPage = prompt.currentPage;
    if (!currentPage) return null;

    let elementId = `ov_app_prompt_page_styles_${currentPage.id}`;
    let existingTag = document.getElementById(elementId);
    if (existingTag) existingTag.remove();

    let styles = generatePromptStyles(prompt, currentPage);
    let innerHTML = styles;

    let tag = document.createElement('style');
    tag.id = elementId;
    tag.innerHTML = innerHTML;
    el.after(tag);
  };

  getPage = () => {
    const { prompt } = this.props;
    prompt.updateBodyOverflow();
    if (!prompt.display) return null;
    if (!prompt.currentPage) return null;

    const page = prompt.currentPage;
    const Page = loadable(() => import('./Page'));
    return (
      <Page
        key={page.id}
        page={page}
        {...this.props}
      />
    )
  };

  getLauncher = () => {
    const { prompt } = this.props;
    const launcher = prompt.launcher;
    if (!prompt.displayLauncher) return null;

    const LauncherContainer = loadable(() => import('./LauncherContainer'));
    return (
      <LauncherContainer
        prompt={prompt}
        launcher={launcher}
        {...this.props}
      />
    )
  };

  render() {
    const { prompt } = this.props;
    const launcher = prompt.launcher;
    const currentPage = prompt.currentPage;
    const cssConfig = currentPage ? currentPage.css : null;

    if (this.state.error && prompt.display && prompt.redirectCheckoutWhenClose) {
      window.location = pageStore.checkoutUrl;
      return null;
    }

    this.updatePromptStyles();
    this.trackView();
    prompt.updateBodyOverflow();

    const className = classNames(
      "ov-prompt",
      {"ov-active": prompt.display && currentPage},
      cssConfig ? cssConfig.cssClasses : "",
    );

    return (
      <div
        id={prompt.id}
        className={className}
      >

        {this.getPage()}
        {this.getLauncher()}

        {cssConfig ? (
          <React.Fragment>
            <style>{`${cssConfig.customCssStyles || ""}`}</style>
          </React.Fragment>
        ): null}
      </div>
    )
  }
};




export default Prompt;


